import { Button, Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/styles";
import { CalendarDots } from "@phosphor-icons/react";
import { useRescheduling } from "../ReschedulingProvider";
import { formatTimeZoneToAbbreviation } from "utils/formatTimeZoneToAbbreviation";
import { formatLocalizedDate } from "utils/formatLocalizedDate";

const mapBillingTypeToDisplay = (t, billingType) => {
  switch (billingType) {
    case "ASSESSMENT":
      return t("dashboard:parent.nextAppointment.billingTypes.assessment");
    case "IN_PERSON_ASSESSMENT":
      return t("dashboard:parent.nextAppointment.billingTypes.assessmentInPerson");
    case "ORIENTATION":
      return t("dashboard:parent.nextAppointment.billingTypes.orientation");
    case "DIRECT_THERAPY":
      return t("dashboard:parent.nextAppointment.billingTypes.directTherapy");
    case "CAREGIVER_TRAINING":
      return t("dashboard:parent.nextAppointment.billingTypes.caregiverTraining");
    default:
      return "";
  }
};

export default function EventSummary({
  HighligthIcon = CalendarDots,
  highlightText,
  onUpdate = undefined,
  eventDate = undefined,
}) {
  const { t } = useTranslation(["rescheduling", "dashboard"]);
  const theme = useTheme();
  const { newAppointmentTime, callInfo } = useRescheduling();
  const selectedEventDate = eventDate || newAppointmentTime;
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneAbbr = formatTimeZoneToAbbreviation(
    userTimezone,
    selectedEventDate ? new Date(selectedEventDate) : undefined
  );
  const clientName = `${callInfo?.name} ${callInfo?.child_last_name}`;
  const clinicianFirstName = callInfo?.clinician_name?.split(" ")?.[0] || "";
  const resolvedHighlightText = highlightText || t("rescheduling:appointmentTime");

  return (
    <Stack
      p={7}
      bgcolor="default.hover"
      gap={5}
      borderRadius="16px"
      border={`1px solid ${theme.palette.divider}`}
    >
      <Stack>
        <Stack direction="row" gap={1} alignItems="center">
          <HighligthIcon color={theme.palette.primary.main} />
          <Typography variant="body2" color="primary.main">
            {resolvedHighlightText}
          </Typography>
        </Stack>
        <Typography variant="h4">
          {mapBillingTypeToDisplay(t, callInfo?.billing_type)} {t("rescheduling:with")}{" "}
          {clinicianFirstName}
        </Typography>
      </Stack>
      <Stack gap={1}>
        <Stack direction="row">
          <Typography variant="body1" color="text.secondary" width="58px" flexShrink={0}>
            {t("rescheduling:when")}
          </Typography>
          <Typography variant="body1">
            {`${formatLocalizedDate(new Date(selectedEventDate), {
              en: "EEEE MMMM do, yyyy @ h:mm a",
              es: "EEEE d 'de' MMMM 'de' yyyy, h:mm a",
            })} (${timezoneAbbr})`}
          </Typography>
        </Stack>
        <Stack direction="row">
          <Typography variant="body1" color="text.secondary" width="58px" flexShrink={0}>
            {t("rescheduling:who")}
          </Typography>
          <Typography variant="body1">
            {clientName}, {callInfo?.clinician_name}
            {callInfo?.secondary_clinician_name && `, ${callInfo.secondary_clinician_name}`}
          </Typography>
        </Stack>
      </Stack>
      {onUpdate && (
        <Button size="large" color="secondary" onClick={onUpdate}>
          {t("rescheduling:updateNow")}
        </Button>
      )}
    </Stack>
  );
}
